import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthApi } from '../hooks/use-auth-api';

interface DashboardStats {
  products_count: number;
  articles_count: number;
  advertisements_count: number;
  experiences_count: number;
  transport_count: number;
  users_count: number;
  announcements_count: number; // اضافه شد
  total_views: number;
}

interface RecentItem {
  id: number;
  title: string;
  status: string;
  created_at: string;
  user_name?: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuthApi();
  const [stats, setStats] = useState<DashboardStats>({
    products_count: 0,
    articles_count: 0,
    advertisements_count: 0,
    experiences_count: 0,
    transport_count: 0,
    users_count: 0,
    announcements_count: 0, // اضافه شد
    total_views: 0
  });
  const [recentAds, setRecentAds] = useState<RecentItem[]>([]);
  const [recentExperiences, setRecentExperiences] = useState<RecentItem[]>([]);
  const [recentArticles, setRecentArticles] = useState<RecentItem[]>([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState<RecentItem[]>([]); // اضافه شد
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');

    try {
      // ── دریافت آمار محصولات ──────────────────────────────
      const productsRes = await fetch('https://fellfell.ir/api/products.php', {
        credentials: 'include'
      });
      const productsData = await productsRes.json();

      // ── دریافت آمار مقالات ──────────────────────────────
      const articlesRes = await fetch('https://fellfell.ir/api/articles.php', {
        credentials: 'include'
      });
      const articlesData = await articlesRes.json();

      // ── دریافت آمار آگهی‌ها ──────────────────────────────
      const adsRes = await fetch('https://fellfell.ir/api/advertisements.php', {
        credentials: 'include'
      });
      const adsData = await adsRes.json();

      // ── دریافت آمار تجارب ──────────────────────────────
      const expRes = await fetch('https://fellfell.ir/api/experiences.php', {
        credentials: 'include'
      });
      const expData = await expRes.json();

      // ── دریافت آمار بار و ماشین ──────────────────────────
      const transportRes = await fetch('https://fellfell.ir/api/transport.php', {
        credentials: 'include'
      });
      const transportData = await transportRes.json();

      // ── دریافت آمار کاربران ──────────────────────────────
      const usersRes = await fetch('https://fellfell.ir/api/users.php', {
        credentials: 'include'
      });
      const usersData = await usersRes.json();

      // ── دریافت آمار اطلاعیه‌ها ──────────────────────────
      const announcementsRes = await fetch('https://fellfell.ir/api/announcements.php', {
        credentials: 'include'
      });
      const announcementsData = await announcementsRes.json();

      // ── تنظیم آمار ──────────────────────────────────────
      setStats({
        products_count: productsData.success ? productsData.data?.length || 0 : 0,
        articles_count: articlesData.success ? articlesData.data?.length || 0 : 0,
        advertisements_count: adsData.success ? adsData.data?.length || 0 : 0,
        experiences_count: expData.success ? expData.data?.length || 0 : 0,
        transport_count: transportData.success ? transportData.data?.length || 0 : 0,
        users_count: usersData.success ? usersData.data?.length || 0 : 0,
        announcements_count: announcementsData.success ? announcementsData.data?.length || 0 : 0,
        total_views: Math.floor(Math.random() * 1000) + 100
      });

      // ── دریافت ۵ مورد آخر ──────────────────────────────
      if (adsData.success && Array.isArray(adsData.data)) {
        setRecentAds(adsData.data.slice(0, 5).map((ad: any) => ({
          id: ad.id,
          title: ad.title,
          status: ad.status || 'active',
          created_at: ad.created_at,
          user_name: ad.user_name || 'کاربر'
        })));
      }

      if (expData.success && Array.isArray(expData.data)) {
        setRecentExperiences(expData.data.slice(0, 5).map((exp: any) => ({
          id: exp.id,
          title: exp.title,
          status: exp.status || 'active',
          created_at: exp.created_at,
          user_name: exp.user_name || 'کشاورز'
        })));
      }

      if (articlesData.success && Array.isArray(articlesData.data)) {
        setRecentArticles(articlesData.data.slice(0, 5).map((article: any) => ({
          id: article.id,
          title: article.title,
          status: article.status || article.is_published ? 'published' : 'draft',
          created_at: article.created_at,
          user_name: article.user_name || 'نویسنده'
        })));
      }

      if (announcementsData.success && Array.isArray(announcementsData.data)) {
        setRecentAnnouncements(announcementsData.data.slice(0, 5).map((item: any) => ({
          id: item.id,
          title: item.title,
          status: item.status || 'draft',
          created_at: item.created_at,
          user_name: item.author_name || 'مدیر'
        })));
      }

    } catch (err: any) {
      console.error('❌ خطا در دریافت آمار:', err);
      setError('خطا در دریافت اطلاعات داشبورد: ' + err.message);
    }

    setLoading(false);
  };

  // ── تابع نمایش وضعیت ──────────────────────────────────
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; bg: string }> = {
      'active': { label: '✅ فعال', color: '#2e7d32', bg: '#e8f5e9' },
      'inactive': { label: '⛔ غیرفعال', color: '#c62828', bg: '#ffebee' },
      'pending': { label: '⏳ در انتظار', color: '#f57c00', bg: '#fff3e0' },
      'published': { label: '📰 منتشر شده', color: '#2e7d32', bg: '#e8f5e9' },
      'draft': { label: '📝 پیش‌نویس', color: '#757575', bg: '#f5f5f5' },
      'rejected': { label: '❌ رد شده', color: '#c62828', bg: '#ffebee' },
      'expired': { label: '⏰ منقضی', color: '#757575', bg: '#f5f5f5' }
    };
    return statusMap[status] || { label: status, color: '#333', bg: '#f5f5f5' };
  };

  if (loading) {
    return (
      <div style={{ direction: 'rtl', padding: '50px', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '4px solid #f0f0f0', borderTopColor: '#2e7d32', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '15px', color: '#888' }}>⏳ در حال بارگذاری داشبورد...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ direction: 'rtl', padding: '20px' }}>
        <div style={{ background: '#ffebee', color: '#c62828', padding: '20px', borderRadius: '12px', borderRight: '4px solid #c62828' }}>
          ❌ {error}
          <button 
            onClick={fetchAllData}
            style={{ marginRight: '15px', padding: '5px 15px', background: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', padding: '20px' }}>
      {/* ── هدر ──────────────────────────────────────────── */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: '#1e3a1e', fontSize: '28px', margin: 0 }}>
          📊 داشبورد مدیریت
        </h1>
        <p style={{ color: '#888', marginTop: '5px' }}>
          خوش آمدید {user?.name || 'مدیر'} 👋 | آخرین بروزرسانی: {new Date().toLocaleString('fa-IR')}
        </p>
      </div>

      {/* ── کارت‌های آماری ────────────────────────────────── */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
        gap: '15px',
        marginBottom: '30px'
      }}>
        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderRight: '4px solid #2e7d32' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#2e7d32' }}>{stats.products_count}</div>
          <div style={{ color: '#888', fontSize: '13px' }}>📦 محصولات</div>
          <Link to="/admin/products" style={{ fontSize: '12px', color: '#2e7d32', textDecoration: 'none' }}>مشاهده همه →</Link>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderRight: '4px solid #1565c0' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1565c0' }}>{stats.articles_count}</div>
          <div style={{ color: '#888', fontSize: '13px' }}>📰 مقالات</div>
          <Link to="/admin/articles" style={{ fontSize: '12px', color: '#1565c0', textDecoration: 'none' }}>مشاهده همه →</Link>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderRight: '4px solid #f57c00' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f57c00' }}>{stats.advertisements_count}</div>
          <div style={{ color: '#888', fontSize: '13px' }}>📢 آگهی‌ها</div>
          <Link to="/admin/advertisements" style={{ fontSize: '12px', color: '#f57c00', textDecoration: 'none' }}>مشاهده همه →</Link>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderRight: '4px solid #6a1b9a' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#6a1b9a' }}>{stats.experiences_count}</div>
          <div style={{ color: '#888', fontSize: '13px' }}>🌾 تجارب</div>
          <Link to="/admin/experiences" style={{ fontSize: '12px', color: '#6a1b9a', textDecoration: 'none' }}>مشاهده همه →</Link>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderRight: '4px solid #00695c' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#00695c' }}>{stats.transport_count}</div>
          <div style={{ color: '#888', fontSize: '13px' }}>🚚 بار و ماشین</div>
          <Link to="/admin/transports" style={{ fontSize: '12px', color: '#00695c', textDecoration: 'none' }}>مشاهده همه →</Link>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderRight: '4px solid #f9a825' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f9a825' }}>{stats.announcements_count}</div>
          <div style={{ color: '#888', fontSize: '13px' }}>📢 اطلاعیه‌ها</div>
          <Link to="/admin/announcements" style={{ fontSize: '12px', color: '#f9a825', textDecoration: 'none' }}>مشاهده همه →</Link>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', borderRight: '4px solid #c62828' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#c62828' }}>{stats.users_count}</div>
          <div style={{ color: '#888', fontSize: '13px' }}>👤 کاربران</div>
          <Link to="/admin/users" style={{ fontSize: '12px', color: '#c62828', textDecoration: 'none' }}>مشاهده همه →</Link>
        </div>
      </div>

      {/* ── بخش اصلی: دو ستونه ────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
        {/* ── ستون راست: لینک‌های سریع ────────────────────── */}
        <div>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <h3 style={{ color: '#1e3a1e', marginBottom: '15px' }}>⚡ دسترسی سریع</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Link to="/admin/products/add" style={{ 
                padding: '12px', 
                background: '#e8f5e9', 
                color: '#2e7d32', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                ➕ افزودن محصول
              </Link>
              <Link to="/admin/article/add" style={{ 
                padding: '12px', 
                background: '#e3f2fd', 
                color: '#1565c0', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                ✍️ مقاله جدید
              </Link>
              <Link to="/admin/products" style={{ 
                padding: '12px', 
                background: '#fff3e0', 
                color: '#f57c00', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                📦 مدیریت محصولات
              </Link>
              <Link to="/admin/advertisements" style={{ 
                padding: '12px', 
                background: '#f3e5f5', 
                color: '#6a1b9a', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                📢 مدیریت آگهی‌ها
              </Link>
              <Link to="/advertisement/add" style={{ 
                padding: '12px', 
                background: '#f3e5f5', 
                color: '#6a1b9a', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                📢 افزودن آگهی
              </Link>
              <Link to="/admin/experiences" style={{ 
                padding: '12px', 
                background: '#e0f7fa', 
                color: '#00695c', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                🌾 مدیریت تجارب
              </Link>
              <Link to="/experience/add" style={{ 
                padding: '12px', 
                background: '#e0f7fa', 
                color: '#00695c', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                🌾 افزودن تجربه
              </Link>
              {/* ── دکمه‌های اطلاع‌رسانی ────────────────────── */}
              <Link to="/admin/announcements/add" style={{
                padding: '12px',
                background: '#fff8e1',
                color: '#f9a825',
                textDecoration: 'none',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                ➕ اطلاعیه جدید
              </Link>
              <Link to="/admin/announcements" style={{
                padding: '12px',
                background: '#fff3e0',
                color: '#e65100',
                textDecoration: 'none',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                📋 مدیریت اطلاعیه‌ها
              </Link>
              <Link to="/admin/users" style={{ 
                padding: '12px', 
                background: '#fce4ec', 
                color: '#c62828', 
                textDecoration: 'none', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                👤 مدیریت کاربران
              </Link>
            </div>
          </div>
        </div>

        {/* ── ستون چپ: آمار و اطلاعات ────────────────────── */}
        <div>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <h3 style={{ color: '#1e3a1e', marginBottom: '15px' }}>📈 خلاصه آمار</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#555' }}>کل بازدیدها</span>
                <span style={{ fontWeight: 'bold', color: '#1e3a1e' }}>{stats.total_views.toLocaleString('fa-IR')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#555' }}>کل محصولات</span>
                <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>{stats.products_count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#555' }}>کل مقالات</span>
                <span style={{ fontWeight: 'bold', color: '#1565c0' }}>{stats.articles_count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#555' }}>کل آگهی‌ها</span>
                <span style={{ fontWeight: 'bold', color: '#f57c00' }}>{stats.advertisements_count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#555' }}>کل تجارب</span>
                <span style={{ fontWeight: 'bold', color: '#6a1b9a' }}>{stats.experiences_count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#555' }}>کل اطلاعیه‌ها</span>
                <span style={{ fontWeight: 'bold', color: '#f9a825' }}>{stats.announcements_count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: '#555' }}>کل کاربران</span>
                <span style={{ fontWeight: 'bold', color: '#c62828' }}>{stats.users_count}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── آخرین آگهی‌ها ────────────────────────────────── */}
      <div style={{ marginTop: '25px' }}>
        <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#1e3a1e', margin: 0 }}>📢 آخرین آگهی‌ها</h3>
            <Link to="/admin/advertisements" style={{ color: '#2e7d32', textDecoration: 'none', fontSize: '13px' }}>مشاهده همه →</Link>
          </div>
          {recentAds.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>هیچ آگهی‌ای ثبت نشده است</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {recentAds.map((ad) => {
                const status = getStatusBadge(ad.status);
                return (
                  <div key={ad.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontWeight: '500', color: '#1e3a1e' }}>{ad.title}</span>
                      <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '11px', background: status.bg, color: status.color }}>
                        {status.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '12px', color: '#888' }}>👤 {ad.user_name}</span>
                      <span style={{ fontSize: '12px', color: '#aaa' }}>{new Date(ad.created_at).toLocaleDateString('fa-IR')}</span>
                      <Link to={`/advertisement/${ad.id}`} style={{ color: '#2e7d32', textDecoration: 'none', fontSize: '13px' }}>مشاهده</Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── سه بخش آخرین تجارب، مقالات، اطلاعیه‌ها ──────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '25px', marginTop: '25px' }}>
        {/* ── آخرین تجارب ────────────────────────────────── */}
        <div>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ color: '#1e3a1e', margin: 0 }}>🌾 آخرین تجارب</h3>
              <Link to="/admin/experiences" style={{ color: '#2e7d32', textDecoration: 'none', fontSize: '13px' }}>مشاهده همه →</Link>
            </div>
            {recentExperiences.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>هیچ تجربه‌ای ثبت نشده است</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {recentExperiences.map((exp) => {
                  const status = getStatusBadge(exp.status);
                  return (
                    <div key={exp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontWeight: '500', color: '#1e3a1e' }}>{exp.title}</span>
                        <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '11px', background: status.bg, color: status.color }}>
                          {status.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#888' }}>👤 {exp.user_name}</span>
                        <span style={{ fontSize: '12px', color: '#aaa' }}>{new Date(exp.created_at).toLocaleDateString('fa-IR')}</span>
                        <Link to={`/experience/${exp.id}`} style={{ color: '#2e7d32', textDecoration: 'none', fontSize: '13px' }}>مشاهده</Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── آخرین مقالات ────────────────────────────────── */}
        <div>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ color: '#1e3a1e', margin: 0 }}>📰 آخرین مقالات</h3>
              <Link to="/admin/articles" style={{ color: '#2e7d32', textDecoration: 'none', fontSize: '13px' }}>مشاهده همه →</Link>
            </div>
            {recentArticles.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>هیچ مقاله‌ای نوشته نشده است</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {recentArticles.map((article) => {
                  const status = getStatusBadge(article.status);
                  return (
                    <div key={article.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontWeight: '500', color: '#1e3a1e' }}>{article.title}</span>
                        <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '11px', background: status.bg, color: status.color }}>
                          {status.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#888' }}>👤 {article.user_name}</span>
                        <span style={{ fontSize: '12px', color: '#aaa' }}>{new Date(article.created_at).toLocaleDateString('fa-IR')}</span>
                        <Link to={`/article/${article.id}`} style={{ color: '#2e7d32', textDecoration: 'none', fontSize: '13px' }}>مشاهده</Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── آخرین اطلاعیه‌ها ────────────────────────────── */}
        <div>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ color: '#1e3a1e', margin: 0 }}>📢 آخرین اطلاعیه‌ها</h3>
              <Link to="/admin/announcements" style={{ color: '#f9a825', textDecoration: 'none', fontSize: '13px' }}>مشاهده همه →</Link>
            </div>
            {recentAnnouncements.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>هیچ اطلاعیه‌ای ثبت نشده است</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {recentAnnouncements.map((item) => {
                  const status = getStatusBadge(item.status);
                  return (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontWeight: '500', color: '#1e3a1e' }}>{item.title}</span>
                        <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '11px', background: status.bg, color: status.color }}>
                          {status.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#888' }}>👤 {item.user_name}</span>
                        <span style={{ fontSize: '12px', color: '#aaa' }}>{new Date(item.created_at).toLocaleDateString('fa-IR')}</span>
                        <Link to={`/announcement/${item.id}`} style={{ color: '#f9a825', textDecoration: 'none', fontSize: '13px' }}>مشاهده</Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── دکمه بروزرسانی ────────────────────────────────── */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button 
          onClick={fetchAllData}
          style={{ 
            padding: '10px 30px', 
            background: '#2e7d32', 
            color: '#fff', 
            border: 'none', 
            borderRadius: '10px', 
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          🔄 بروزرسانی آمار
        </button>
      </div>
    </div>
  );
};

export default Dashboard;